import { ChatRequest, ChatResponse } from '../types/chat';

export class ChatService {
  private baseUrl: string;
  private streamingUrl: string; // Direct ALB URL for streaming (bypasses CloudFront)

  constructor() {
    // Use local backend for development, CloudFront for production
    // Always use HTTPS in production to avoid mixed content errors
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.baseUrl = 'http://localhost:8000';
      this.streamingUrl = 'http://localhost:8000';
    } else {
      // Production: Use CloudFront for regular API, ALB for streaming (when HTTPS is available)
      const envUrl = process.env.REACT_APP_API_URL;
      const albDomain = process.env.REACT_APP_ALB_DOMAIN; // Custom domain for ALB (e.g., api.yourdomain.com)
      
      if (envUrl && envUrl.startsWith('https://')) {
        this.baseUrl = envUrl;
      } else {
        // Default to CloudFront HTTPS
        this.baseUrl = 'https://d3iaknzfhx8mxa.cloudfront.net';
      }
      
      // For streaming: Use ALB HTTPS if custom domain is configured, otherwise use CloudFront
      if (albDomain) {
        this.streamingUrl = `https://${albDomain}`;
        console.log('[CHAT SERVICE] Using ALB HTTPS for streaming:', this.streamingUrl);
      } else {
        // Fallback to CloudFront (will be buffered, but works)
        this.streamingUrl = this.baseUrl;
        console.log('[CHAT SERVICE] Using CloudFront for streaming (may be buffered):', this.streamingUrl);
      }
    }
    console.log('[CHAT SERVICE] ChatService initialized with baseUrl:', this.baseUrl);
    console.log('[CHAT SERVICE] Streaming URL:', this.streamingUrl);
    console.log('[CHAT SERVICE] Initialization timestamp:', new Date().toISOString());
  }

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChatResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error in ChatService.sendMessage:', error);
      throw error;
    }
  }

  async sendMessageStream(
    request: ChatRequest,
    onChunk: (chunk: any) => void,
    onComplete: (response: ChatResponse) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    console.log('[STREAMING] sendMessageStream called at', new Date().toISOString());
    console.log('[STREAMING] Request:', JSON.stringify(request, null, 2));
    try {
      // Use streamingUrl (ALB HTTPS if configured, otherwise CloudFront)
      const streamUrl = `${this.streamingUrl}/api/chat/stream`;
      console.log('[STREAMING] Streaming to URL:', streamUrl);
      console.log('[STREAMING] Base URL:', this.baseUrl);
      console.log('[STREAMING] Streaming URL (bypasses CloudFront if ALB domain configured):', this.streamingUrl);
      
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Streaming response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullMessage = '';
      let conversationId = '';
      let codeSnippets: any[] = [];

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      let chunkCount = 0;
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('[STREAMING] SSE: stream ended, total chunks:', chunkCount);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'conversation_id') {
                conversationId = data.conversation_id;
                console.log('[STREAMING] SSE: conversation_id', conversationId);
                onChunk({ type: 'conversation_id', conversation_id: conversationId });
              } else if (data.type === 'timing') {
                console.log('[STREAMING] SSE: timing', data.stage, data.time);
                onChunk(data);
              } else if (data.type === 'content') {
                const text = data.text || '';
                fullMessage += text;
                chunkCount++;
                console.log(`[STREAMING] SSE: content chunk #${chunkCount}, text: "${text.substring(0, 50)}", total: ${fullMessage.length}`);
                // Call onChunk immediately for each content chunk - no delay
                // flushSync in App.tsx will handle immediate UI updates
                onChunk({ type: 'content', text: text });
              } else if (data.type === 'done') {
                fullMessage = data.message || fullMessage;
                codeSnippets = data.code_snippets || [];
                console.log('[STREAMING] SSE: done, final message length:', fullMessage.length, 'total chunks:', chunkCount);
                onChunk(data);
              } else if (data.type === 'error') {
                console.error('SSE: error', data.message);
                onError(new Error(data.message || 'Unknown error'));
                return;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Line:', line.substring(0, 100));
            }
          } else if (line.trim() !== '' && !line.startsWith(':')) {
            // Log non-data lines for debugging (but limit logging)
            console.log('[STREAMING] SSE: non-data line:', line.substring(0, 50));
          }
        }
      }

      onComplete({
        message: fullMessage,
        conversation_id: conversationId,
        timestamp: new Date().toISOString(),
        code_snippets: codeSnippets,
      });
    } catch (error) {
      console.error('Error in ChatService.sendMessageStream:', error);
      onError(error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  async getConversationHistory(conversationId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat/history/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in ChatService.getConversationHistory:', error);
      throw error;
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      console.error('Error in ChatService.healthCheck:', error);
      return false;
    }
  }
}
